import { useEffect, useRef, useState } from "react";

import { cn } from "../foundations/cn.js";
import { onChecks, onFrames } from "../foundations/frameLoop.js";
import { type BoxSize, onResize } from "../foundations/onResize.js";
import { useMediaQuery } from "../foundations/useMediaQuery.js";
import { useOnScreen } from "../foundations/useOnScreen.js";
import { RARITY_TEXT, type RarityTier } from "./rarity.js";
import {
  HALFTONE,
  HOLO,
  halftoneDots,
  halftoneFurthest,
  halftoneMoment,
  halftonePhases,
  holoBrightness,
  holoLight,
  holoPalette,
  type PlacedHalftoneDot,
  placedHalftoneRadius,
  placeHalftoneDot,
  type Rgb,
  seededRandom,
  tierAtLeast,
  wearsHolo,
} from "./rarityLook.js";

/** A canvas's pixels per CSS pixel, at most: the dots and the band are soft, so 2x is plenty. */
const MAX_PIXEL_RATIO = 2;
/**
 * The halftone's and the band's frames while the card moves, at most every this many ms: one
 * card moves at a time (the hovered or focused one), so its waves can be smooth.
 */
const HALFTONE_FRAME_MS = 33;
const HOLO_FRAME_MS = 40;

/**
 * The glow's light per tier: the tier's color at its middle, a third of it just past halfway, then
 * nothing. Complete literals, so Tailwind sees them.
 */
const GLOW_LIGHT: Record<RarityTier, string> = {
  common: "from-rarity-common via-rarity-common/35",
  uncommon: "from-rarity-uncommon via-rarity-uncommon/35",
  rare: "from-rarity-rare via-rarity-rare/35",
  epic: "from-rarity-epic via-rarity-epic/35",
  legendary: "from-rarity-legendary via-rarity-legendary/35",
  chase: "from-rarity-chase via-rarity-chase/35",
};

/** The foot band's fill: below Rare the tier's color, lit at its middle; from Rare, under the holo. */
const BAND_FILL: Record<RarityTier, string> = {
  common: "band-lit bg-rarity-common",
  uncommon: "band-lit bg-rarity-uncommon",
  rare: "bg-rarity-rare",
  epic: "bg-rarity-epic",
  legendary: "bg-rarity-legendary",
  chase: "bg-rarity-chase",
};

export interface RarityEffectsProps {
  tier: RarityTier;
  /** The card's place in its row: where its halftone and band are in their motion. */
  seed: number;
  /** Hovered or focused: the halftone and band drift and the glow deepens. */
  active: boolean;
}

/**
 * An item dressed in its rarity, drawn into the card it sits in: a glow of its tier behind the
 * art, a halftone of the tier's color round it, a band of the tier at the card's foot
 * (holographic from Rare) and, from Epic, a sheen crossing the card on hover. Its holder is the
 * `group/item`, positioned, isolated and clipped (`relative isolate overflow-hidden`), and drives
 * `active` and `seed` from `useRarityWake`, so anything wearing the look (an ItemCard with
 * `effects`, a hot pull in chat) gets the same costs: drawn once and still at rest, moving only
 * while awake, on the shared frame loop at a capped rate, never under reduced motion, and checking
 * nothing off screen.
 */
export function RarityEffects({ tier, seed, active }: RarityEffectsProps) {
  const still = useMediaQuery("(prefers-reduced-motion: reduce)");
  const moving = active && !still;
  // Its backdrop as a state, for the shared on-screen watch: only a card on screen checks its color.
  const [backdrop, setBackdrop] = useState<HTMLSpanElement | null>(null);
  const onScreen = useOnScreen(backdrop);
  return (
    <>
      {/* The tier's light and halftone behind the art, cut to the card's corners. */}
      <span
        ref={setBackdrop}
        aria-hidden
        className="pointer-events-none absolute inset-0 -z-1 overflow-hidden rounded-[inherit]"
      >
        <span className="absolute top-0 left-0 aspect-square max-h-full w-full overflow-hidden opacity-20 blur-[10px] transition-opacity duration-slow ease-standard group-hover/item:opacity-40">
          <span
            className={cn(
              "absolute inset-0 origin-[50%_45%] scale-[110%] bg-radial-[74.9%_60.1%_at_50%_45%] from-0% via-55% to-transparent transition-transform duration-slow ease-standard motion-safe:group-hover/item:scale-[132%]",
              GLOW_LIGHT[tier],
            )}
          />
        </span>
        <span
          className={cn("absolute top-0 left-0 aspect-square max-h-full w-full", RARITY_TEXT[tier])}
        >
          <Halftone seed={seed} still={still} moving={moving} watching={onScreen} />
        </span>
      </span>
      <span
        aria-hidden
        className={cn(
          "pointer-events-none absolute bottom-0 left-1/2 z-1 h-0.5 w-[36%] -translate-x-1/2 overflow-hidden rounded-t-full",
          BAND_FILL[tier],
          RARITY_TEXT[tier],
        )}
      >
        {wearsHolo(tier) && (
          <HoloBand seed={seed} still={still} moving={moving} watching={onScreen} />
        )}
      </span>
      {tierAtLeast(tier, "epic") && (
        <span aria-hidden className={cn("rarity-sheen", RARITY_TEXT[tier])} />
      )}
    </>
  );
}

/** A canvas's pixel ratio. */
function pixelRatio(): number {
  return Math.min(MAX_PIXEL_RATIO, window.devicePixelRatio || 1);
}

/** Sizes `canvas` for a box of `size` CSS px at `ratio`, at least one pixel each way. */
function sizeCanvas(canvas: HTMLCanvasElement, size: BoxSize, ratio: number): void {
  canvas.width = Math.max(1, Math.round(size.width * ratio));
  canvas.height = Math.max(1, Math.round(size.height * ratio));
}

/** A 1px canvas that turns any CSS color into sRGB channels, made on first use. */
let swatch: CanvasRenderingContext2D | null | undefined;

/** `color` (a computed CSS color) as sRGB channels, read by painting a pixel. */
function channels(color: string): Rgb {
  swatch ??= document.createElement("canvas").getContext("2d", { willReadFrequently: true });
  if (!swatch) return [0, 0, 0];
  swatch.clearRect(0, 0, 1, 1);
  swatch.fillStyle = color;
  swatch.fillRect(0, 0, 1, 1);
  const [r = 0, g = 0, b = 0] = swatch.getImageData(0, 0, 1, 1).data;
  return [r, g, b];
}

/**
 * Reads `element`'s text color into `onColor` now, and returns the check that does it again
 * whenever the color has changed (a theme switch, a tenant's colors).
 */
function colorCheck(element: Element, onColor: (color: string) => void): () => void {
  let color = "";
  const check = () => {
    const next = getComputedStyle(element).color;
    if (next === color) return;
    color = next;
    onColor(next);
  };
  check();
  return check;
}

/**
 * Runs a canvas's color check on the shared timer while `watching` (its card on screen), and once
 * as it starts, so a theme switched while the card was away reaches it as it returns.
 */
function useColorChecks(check: { current: () => void }, watching: boolean) {
  useEffect(() => {
    if (!watching) return;
    check.current();
    return onChecks(() => check.current());
  }, [check, watching]);
}

/** Draws on the shared frame loop while `moving`; still, a canvas draws only as its size or color arrives. */
function useFrames(drawing: { current: (seconds: number) => void }, moving: boolean, ms: number) {
  useEffect(() => {
    if (!moving) return;
    return onFrames((now) => drawing.current(now / 1000), ms);
  }, [drawing, moving, ms]);
}

interface CanvasLayerProps {
  /** The card's place in its row: where its layers are in their motion. */
  seed: number;
  /** Drawn once, never moving: reduced motion. */
  still: boolean;
  /** Moving on the frame loop: hovered or focused, and not still. */
  moving: boolean;
  /** Checking its color for a change: its card is on screen. */
  watching: boolean;
}

/**
 * The halftone: the tier's color (its holder's text color) in two layers of dots on one grid round
 * the art's center, each on its own clock, 23% strong and 37% while the card is hovered.
 */
function Halftone({ seed, still, moving, watching }: CanvasLayerProps) {
  const refs = useRef<(HTMLCanvasElement | null)[]>([]);
  const drawing = useRef<(seconds: number) => void>(() => {});
  const checking = useRef<() => void>(() => {});
  useEffect(() => {
    const layers = refs.current.flatMap((canvas) => {
      const context = canvas?.getContext("2d");
      return canvas && context ? [{ canvas, context }] : [];
    });
    const holder = layers[0]?.canvas.parentElement;
    if (!holder) return;
    const phases = halftonePhases(seed, layers.length);
    let size: BoxSize = { width: 0, height: 0 };
    let ratio = 1;
    let color = "";
    let dots: PlacedHalftoneDot[] = [];
    const draw = (seconds: number) => {
      if (size.width === 0 || color === "") return;
      for (const [layer, { canvas, context }] of layers.entries()) {
        context.setTransform(ratio, 0, 0, ratio, 0, 0);
        context.clearRect(0, 0, canvas.width, canvas.height);
        const path = new Path2D();
        const moment = halftoneMoment(size.width, seconds + (phases[layer] ?? 0), still);
        for (const dot of dots) {
          const r = placedHalftoneRadius(dot, moment);
          if (r < 0.25) continue;
          path.moveTo(dot.x + r, dot.y);
          path.arc(dot.x, dot.y, r, 0, Math.PI * 2);
        }
        context.fillStyle = color;
        context.fill(path);
      }
    };
    const redraw = () => draw(still ? 0 : performance.now() / 1000);
    const stopResize = onResize(holder, (next) => {
      size = next;
      ratio = pixelRatio();
      for (const { canvas } of layers) sizeCanvas(canvas, size, ratio);
      // The holder is the art's square, so the art's center is the square's.
      const center = { x: size.width / 2, y: size.width / 2 };
      const furthest = halftoneFurthest(size.width, still);
      dots = halftoneDots(size.width, size.height, center, HALFTONE.cell).flatMap((dot) => {
        const distance = Math.hypot(dot.x - center.x, dot.y - center.y);
        return distance < furthest ? [placeHalftoneDot(dot, distance)] : [];
      });
      redraw();
    });
    checking.current = colorCheck(holder, (next) => {
      color = next;
      redraw();
    });
    drawing.current = draw;
    return () => {
      stopResize();
      checking.current = () => {};
      drawing.current = () => {};
    };
  }, [seed, still]);
  useColorChecks(checking, watching);
  useFrames(drawing, moving, HALFTONE_FRAME_MS);
  return [0, 1].map((layer) => (
    <canvas
      key={layer}
      ref={(canvas) => {
        refs.current[layer] = canvas;
      }}
      className="absolute inset-0 block size-full opacity-[0.23] transition-opacity duration-slow ease-standard group-hover/item:opacity-[0.37]"
    />
  ));
}

/**
 * The foot band's holographic light, pixel by pixel in the tier's color (its holder's text
 * color): slow drifting patches over a fine foil grain, each card its own.
 */
function HoloBand({ seed, still, moving, watching }: CanvasLayerProps) {
  const ref = useRef<HTMLCanvasElement>(null);
  const drawing = useRef<(seconds: number) => void>(() => {});
  const checking = useRef<() => void>(() => {});
  useEffect(() => {
    const canvas = ref.current;
    const context = canvas?.getContext("2d");
    const holder = canvas?.parentElement;
    if (!canvas || !context || !holder) return;
    let image: ImageData | null = null;
    let grain = new Float32Array(0);
    let palette = new Uint8ClampedArray(0);
    const draw = (seconds: number) => {
      if (image === null || palette.length === 0) return;
      const { width, height, data } = image;
      const time = (seconds + seed * 3.1) * HOLO.pace;
      const light = Float32Array.from({ length: width }, (_, x) =>
        holoLight(x / width, time, seed),
      );
      for (let y = 0; y < height; y++) {
        const fromTop = y / Math.max(1, height - 1);
        for (let x = 0; x < width; x++) {
          const pixel = y * width + x;
          const level = holoBrightness(light[x] ?? 0, fromTop, grain[pixel] ?? 0);
          const at = Math.round(level * 255) * 3;
          data[pixel * 4] = palette[at] ?? 0;
          data[pixel * 4 + 1] = palette[at + 1] ?? 0;
          data[pixel * 4 + 2] = palette[at + 2] ?? 0;
          data[pixel * 4 + 3] = 255;
        }
      }
      context.putImageData(image, 0, 0);
    };
    const redraw = () => draw(still ? 0 : performance.now() / 1000);
    const stopResize = onResize(holder, (size) => {
      sizeCanvas(canvas, size, pixelRatio());
      image = context.createImageData(canvas.width, canvas.height);
      const random = seededRandom(seed + 0.5);
      grain = Float32Array.from({ length: canvas.width * canvas.height }, () => random() * 2 - 1);
      redraw();
    });
    checking.current = colorCheck(holder, (color) => {
      palette = holoPalette(channels(color));
      redraw();
    });
    drawing.current = draw;
    return () => {
      stopResize();
      checking.current = () => {};
      drawing.current = () => {};
    };
  }, [seed, still]);
  useColorChecks(checking, watching);
  useFrames(drawing, moving, HOLO_FRAME_MS);
  return <canvas ref={ref} className="absolute inset-0 block size-full" />;
}
